import { defineModule } from '../utils/helpers';

interface AosOptions extends IntersectionObserverInit {
  once: boolean;
  threshold: number;
  selector: string;
  animateClassName: string;
}

// threshold 0: a fraction leaves elements taller than the viewport stuck at
// their opacity-0 start state, because they can never reach it.
const OPTIONS: AosOptions = {
  once: true,
  threshold: 0,
  selector: '[data-aos]',
  animateClassName: 'aos-animate',
};

// Elements animate 100px into the viewport. The inset is dropped on a page with
// less than that to scroll, and the bottom inset stays 0: either way the edge
// would sit somewhere the page can never reach, stranding whatever is behind it.
const TOP_INSET = 100;

const getRootMargin = (): string => {
  const scrollable =
    document.documentElement.scrollHeight > window.innerHeight + TOP_INSET;

  return `${scrollable ? -TOP_INSET : 0}px 50% 0px`;
};

const handleEntries: IntersectionObserverCallback = (entries, instance) => {
  const { once, animateClassName } = OPTIONS;

  entries.forEach(({ target, isIntersecting }) => {
    if (!(target instanceof HTMLElement)) return;

    const shouldRepeat =
      target.hasAttribute('data-aos-repeat') ||
      !(target.hasAttribute('data-aos-once') || once);

    if (isIntersecting && !target.classList.contains(animateClassName)) {
      target.classList.add(animateClassName);

      if (!shouldRepeat) {
        instance.unobserve(target);
      }
    } else if (shouldRepeat) {
      target.classList.remove(animateClassName);
    }
  });
};

let observer: IntersectionObserver | null = null;

export default defineModule(
  () => {
    const { selector, animateClassName, root, threshold } = OPTIONS;

    observer = new IntersectionObserver(handleEntries, {
      root,
      rootMargin: getRootMargin(),
      threshold,
    });

    [...document.querySelectorAll<HTMLElement>(selector)]
      .filter((element) => !element.classList.contains(animateClassName))
      .forEach((element) => observer?.observe(element));
  },
  () => {
    observer?.disconnect();
    observer = null;
  },
);
